import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,  
} from '@angular/forms';
import {
  FormlyFieldConfig,
  FormlyFormOptions,
  FormlyModule,
} from '@ngx-formly/core';
import { FormlyPrimeNGModule } from '@ngx-formly/primeng';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CommonModule } from '@angular/common';
import { ChipsModule } from 'primeng/chips';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { SharedService } from '../../services/shared.service';
import { CreateUserRequest } from '../../interface/license-manager';
import { LicenseUserViewModel } from '../../interface/License-details';
import { LottieAnimationPlayerModule } from '@stantec/basin/lottie-animation-player';

@Component({
  selector: 'app-add-users-modal',

  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlyPrimeNGModule,
    ChipsModule,
    ButtonModule,
    LottieAnimationPlayerModule,
  ],

  templateUrl: './add-users-modal.component.html',
  styleUrls: ['./add-users-modal.component.scss'],
})
export class AddUsersModalComponent implements OnInit {
  form: FormGroup;
  model: any = {};
  options: FormlyFormOptions = {};
  licenseManagerFields: FormlyFieldConfig[] = [];
  isManagerMode = false; // default mode
  mode!: string;
  licenseNumber!: string;
  licenseUsers: LicenseUserViewModel[]=[];
  maxTierUsers!: number;
  inviteLinkValue: any;
  maxValue: number=100;
   public isPageVisible: boolean = true;

  constructor(
    private fb: FormBuilder,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public sharedService: SharedService,
    private messageService: MessageService
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit(): void {
    console.log('IsManager '+ this.isManagerMode );
    this.inviteLinkValue = this.config.data.applicationBaseUrl;  // 'https://dfp-dev.insight.stantec.com/';
    this.licenseNumber = this.config.data.licenseNumber;
    this.licenseUsers = this.config.data.licenseUsers;
    this.maxTierUsers = this.config.data.tierMaxUsers;
    this.mode = this.config.data.mode;
    if(this.mode==='manager'){
      this.isManagerMode = true;
      this.maxValue=1;
    }
    console.log('IsManager '+ this.isManagerMode );
    console.log(this.licenseNumber);

    this.initializeFields();
  }

  initializeFields(): void {
    this.licenseManagerFields = [
      {
        fieldGroupClassName: 'p-fluid p-formgrid p-grid',
        fieldGroup: [
          {
            className: 'p-col-12 p-md-12',
            key: 'emailList',
            type: 'chips',
            props: {
              label: this.isManagerMode ? 'Enterprise License Manager' : 'Email Address',
              placeholder: 'Enter email and press enter',
              required: true,
              maxLength: 255,
              separator: ',',
              addOnBlur: true,
              max: this.maxValue // 1 for manager, 100 for users
            },
          },
        ],
      },
    ];
  }

  copyToClipboard(): void {
    const url = this.inviteLinkValue;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Copied!',
          detail: 'Invite link copied to clipboard',
        });
      })
      .catch(() => {
        this.messageService.add({
          severity: 'error',
          summary: 'Failed',
          detail: 'Could not copy link',
        });
      });
  }

 checkDuplicates(emails: string[]): string[] {
    const uniqueEmails = new Set<string>();
    const duplicateEmails: string[] = [];

    emails.forEach(email => {
      if (uniqueEmails.has(email)) {
        duplicateEmails.push(email);
      } else {
        uniqueEmails.add(email);
      }
    });

    return duplicateEmails;
  }

onSave(): void {
  console.log(this.form.value);
  this.isPageVisible = false;

  if (!this.form.valid) {
    this.messageService.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Form is invalid.',
    });
    this.isPageVisible = true;
    return;
  }

  const emails: string[] = this.form.value.emailList || [];
  const existingEmails = this.getExistingEmails();

  const invalidEmails = this.getInvalidEmails(emails);
  if (invalidEmails.length > 0) {
    this.setFormErrors('invalidEmails');
    this.showMessage('error', 'Invalid Emails', `These emails are not valid: ${invalidEmails.join(', ')}`);
    this.isPageVisible = true;
    return;
  }

  const duplicateEmails = this.checkDuplicates(emails);
  if (duplicateEmails.length > 0) {
    this.setFormErrors('duplicateEmails');
    this.showMessage('error', 'Duplicate Emails', `Duplicate emails found: ${duplicateEmails.join(', ')}`);
    this.isPageVisible = true;
    return;
  }

  const validFormatEmails = emails.filter(email => this.isValidEmail(email));
  const uniqueEmails = validFormatEmails.filter(email => !existingEmails.includes(email.toLowerCase()));

  if (validFormatEmails.length === 0) {
    this.showMessage('error', 'Validation Error', 'Email address is not valid.');
    this.isPageVisible = true;
    return;
  }

  if (uniqueEmails.length === 0) {
    this.showMessage('error', 'Validation Error', 'Entered emails already exist or are invalid.');
    this.isPageVisible = true;
    return;
  }

  if (this.mode === 'user' && this.exceedsMaxUsers(uniqueEmails.length)) {
    const allowedToAdd = this.maxTierUsers - this.licenseUsers.length;
    this.showMessage(
      'error',
      'Validation Error',
      `You have entered more emails than the maximum allowed users (${allowedToAdd}). Please reduce the number of emails.`
    );
    this.isPageVisible = true;
    return;
  }

  const { successMessage, errorMessage } = this.getMessages(uniqueEmails.length);

  const users: CreateUserRequest = {
    licenseNumber: this.licenseNumber,
    emails: uniqueEmails,
    IsManager: this.isManagerMode,
  };

  this.sharedService.addUsers(users).subscribe({
    next: (response) => {
      const apiMessage = response?.message ?? successMessage;
      if (apiMessage.includes('added successfully')) {
        this.showMessage('success', 'Success', successMessage);
      } else {
        this.showMessage('error', 'Error', apiMessage);
      }
      this.ref.close(true);
    },
    error: () => {
      this.showMessage('error', 'Error', errorMessage);
      this.ref.close(false);
    },
  });

  this.isPageVisible = false;
}

// Helper functions:

private getExistingEmails(): string[] {
  return this.licenseUsers
    .filter(user => user.email)
    .map(user => user.email.toLowerCase());
}

private getInvalidEmails(emails: string[]): string[] {
  return emails.filter(email => !this.isValidEmail(email));
}

private setFormErrors(errorType: string): void {
  this.form.setErrors({ [errorType]: true });
}

private showMessage(severity: string, summary: string, detail: string): void {
  this.messageService.add({ severity, summary, detail });
}

private exceedsMaxUsers(newUsersCount: number): boolean {
  return this.maxTierUsers !== -1 && (this.licenseUsers.length + newUsersCount) > this.maxTierUsers;
}

private getMessages(uniqueEmailCount: number): { successMessage: string; errorMessage: string } {
  if (this.isManagerMode) {
    return {
      successMessage: 'License Manager added successfully',
      errorMessage: 'Failed to add License Manager',
    };
  }
  if (uniqueEmailCount === 1) {
    return {
      successMessage: 'User successfully added to Enterprise License.',
      errorMessage: 'Failed to add User',
    };
  }
  return {
    successMessage: 'Users successfully added to Enterprise License.',
    errorMessage: 'Failed to add Users',
  };
}

  isValidEmail(email: string): boolean {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  }

  onCancel(): void {
    this.ref.close();
  }
  IsTextEntered(): boolean {
    return this.form.valid;   
  }
}
